//
// Orchestration push côté page (spec 13 §7/§9) : relie le wrapper Firebase
// (`messaging.ts`) à l'API (`/push/*`). Aucune décision d'UI ici — juste les
// gestes « obtenir un token », « (dé)s'abonner », « revalider ». La PERMISSION est
// demandée par le composant DANS le geste utilisateur (exigé par iOS) ; ces
// fonctions supposent la permission déjà accordée.
//
import type { PushConfigDto, PushDeviceDto } from '@redpandev/shared';
import { api } from '../api';
import { detectPlatform } from '../pwa-install';
import { toPushPlatform } from './support';

// `messaging.ts` (le SEUL fichier à importer `firebase/*`) est chargé en IMPORT
// DYNAMIQUE : le SDK Firebase (~qq dizaines de Ko gzip) part ainsi dans un chunk
// séparé, JAMAIS dans le bundle principal — un visiteur anonyme ne le télécharge
// pas. Il n'arrive que lorsqu'on abonne/revalide/désabonne réellement un appareil.
const loadMessaging = () => import('./messaging');

// Config web Firebase mémoïsée (publique, servie par GET /push/config) : une seule
// sonde par session, partagée par le toggle, la liste et la revalidation au boot.
let configPromise: Promise<PushConfigDto> | null = null;

export function loadPushConfig(): Promise<PushConfigDto> {
  configPromise ??= api.getPushConfig();
  return configPromise;
}

/** Réinitialise le cache de config — TESTS uniquement. */
export function resetPushClientCache(): void {
  configPromise = null;
}

/** L'enregistrement SW de vite-plugin-pwa (réutilisé par `getToken`, pas de 2e SW). */
async function swRegistration(): Promise<ServiceWorkerRegistration> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    throw new Error('no_service_worker');
  }
  return navigator.serviceWorker.ready;
}

/**
 * sha256 tronqué (16 hex) du token — MÊME dérivation que le serveur
 * (`hashPushToken`) pour reconnaître « cet appareil » dans la liste sans jamais
 * exposer le token (spec 13 §4). Web Crypto (`crypto.subtle`).
 */
export async function hashPushTokenHex(token: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

/** Token FCM courant SANS enregistrer côté serveur — pour le badge « cet appareil ». */
export async function currentTokenHash(): Promise<string | null> {
  const [config, { requestPushToken }] = await Promise.all([loadPushConfig(), loadMessaging()]);
  const token = await requestPushToken(config, await swRegistration());
  return token ? hashPushTokenHex(token) : null;
}

/**
 * Abonne CET appareil : `getToken` + upsert idempotent `POST /push/devices`
 * (revalidation comprise). Renvoie l'appareil + le hash du token, ou `null` si
 * aucun token (permission absente).
 */
export async function subscribeThisDevice(): Promise<{
  device: PushDeviceDto;
  tokenHash: string;
} | null> {
  const [config, { requestPushToken }] = await Promise.all([loadPushConfig(), loadMessaging()]);
  const token = await requestPushToken(config, await swRegistration());
  if (!token) return null;
  const device = await api.registerPushDevice({
    token,
    platform: toPushPlatform(detectPlatform()),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
  return { device, tokenHash: await hashPushTokenHex(token) };
}

/** Désabonne CET appareil : `deleteToken()` local (best-effort). Le DELETE
 *  serveur de la ligne appareil est fait par l'appelant (il en a l'`id`). */
export async function revokeLocalToken(): Promise<void> {
  const [config, { revokePushToken }] = await Promise.all([loadPushConfig(), loadMessaging()]);
  await revokePushToken(config);
}
